import React, { useRef, useState, useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import { createPortal } from "react-dom"
import { Flex, Typography } from "antd"
import { Doughnut } from "react-chartjs-2"
import { DONUT_COLORS, doughnutOptions } from "../../constants"
import styles from "../../style.module.css"

const { Text } = Typography

export default function DonutChart({ labels, values }) {
  const chartRef = useRef(null)
  const [tooltip, setTooltip] = useState(null)

  const externalTooltip = useCallback((context) => {
    const { tooltip: tip, chart } = context
    if (tip.opacity === 0) {
      setTooltip(null)
      return
    }
    const dp = tip.dataPoints?.[0]
    if (!dp) return
    const rect = chart.canvas.getBoundingClientRect()
    setTooltip({
      label: dp.label,
      value: dp.formattedValue,
      color: dp.dataset.backgroundColor[dp.dataIndex],
      x: rect.left + tip.caretX,
      y: rect.top + tip.caretY - 12,
    })
  }, [])

  useEffect(() => {
    const canvas = chartRef.current?.canvas
    if (!canvas) return
    const onMove = (e) => {
      setTooltip((prev) => (prev ? { ...prev, x: e.clientX, y: e.clientY - 12 } : prev))
    }
    const onLeave = () => setTooltip(null)
    canvas.addEventListener("mousemove", onMove)
    canvas.addEventListener("mouseleave", onLeave)
    return () => {
      canvas.removeEventListener("mousemove", onMove)
      canvas.removeEventListener("mouseleave", onLeave)
    }
  }, [labels.length])

  const total = values.reduce((s, v) => s + (v ?? 0), 0)

  const chartData = {
    labels,
    datasets: [{
      data: values,
      backgroundColor: labels.map((_, i) => DONUT_COLORS[i % DONUT_COLORS.length]),
      borderWidth: 0,
      hoverOffset: 4
    }]
  }

  const chartOptions = {
    ...doughnutOptions,
    plugins: {
      ...doughnutOptions.plugins,
      tooltip: { enabled: false, external: externalTooltip },
    },
  }

  return (
    <>
      <Flex align="center" gap="var(--spacing-m)" className={styles.donutWrapper}>
        <div className={styles.donutChart}>
          <Doughnut ref={chartRef} data={chartData} options={chartOptions} />
        </div>
        <Flex vertical gap={6} className={styles.donutLegend}>
          {labels.map((label, i) => (
            <Flex key={label ?? i} align="center" gap={6} className={styles.legendItem}>
              <div className={styles.legendDot} style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
              <Text className={styles.legendLabel}>{label}</Text>
              <Text className={styles.textSm}>{total > 0 ? Math.round(((values[i] ?? 0) / total) * 100) : 0}%</Text>
            </Flex>
          ))}
        </Flex>
      </Flex>
      {tooltip && createPortal(
        <div
          className={styles.donutTooltipPortal}
          style={{ left: tooltip.x, top: tooltip.y, transform: "translate(-50%, -100%)" }}
        >
          <span className={styles.donutTooltipSwatch} style={{ background: tooltip.color }} />
          <span>{tooltip.label}: <strong>{tooltip.value}</strong></span>
        </div>,
        document.body
      )}
    </>
  )
}

DonutChart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  values: PropTypes.arrayOf(PropTypes.number).isRequired,
}
